import * as React from 'react';
import { LuFileText, LuImage, LuLibrary, LuPaperclip, LuPlus, LuTable, LuX } from 'react-icons/lu';
import { AccentDialog } from '../../dialogs/AccentDialog';
import { AccentButton } from '../AccentButton';
import { showDialog, t } from '../../services/HelperService';
import { fetchCustomizationAssetThumbnail, listCustomizationAssets } from './customizationApi';
import { ASSET_PURPOSE_REFERENCE, ASSET_PURPOSE_RUNTIME, CustomizationAssetDlg, describeAssetMeasure } from './CustomizationAssetDlg';

/**
 * The customisation's DEPENDENT ASSETS: the asset-library entries it is designed from (design references - a
 * supplier's API manual, a field spreadsheet, a brand guide) and, where the host embeds assets at run time, the
 * runtime ones. Stored on the definition as `dependentAssets: [{ key, purpose, designedFromHash }]`
 * (CustomizationDependentAssets, C#); the AI session reads the references' content through the
 * dependentAssets lane, so a session that opens with them never has the documentation re-pasted.
 *
 * designedFromHash is the library entry's content hash when the outputs were last designed against it. A chip
 * shows "changed" when the library copy differs - the assistant is told the same (STALE) - and the host refreshes
 * the hashes when it accepts the session's work (refreshDesignedFromHashes).
 */

const safeString = (v) => (v ?? '').toString();

/** The dependent assets as the editor holds them: an array of { key, purpose, designedFromHash } from an array, a JSON string, or nothing. */
export const normalizeDependentAssets = (input) => {
  let list = input;
  if (typeof list === 'string') {
    const text = list.trim();
    if (!text) return [];
    try {
      list = JSON.parse(text);
    } catch {
      return [];
    }
  }
  if (list && !Array.isArray(list) && Array.isArray(list.dependentAssets)) list = list.dependentAssets;
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const result = [];
  list.forEach((entry) => {
    if (!entry || typeof entry !== 'object') return;
    const key = safeString(entry.key ?? entry.Key).trim();
    if (!key || seen.has(key)) return;
    seen.add(key);
    const purposeRaw = safeString(entry.purpose ?? entry.Purpose).trim().toLowerCase();
    const purpose = purposeRaw === ASSET_PURPOSE_RUNTIME ? ASSET_PURPOSE_RUNTIME : ASSET_PURPOSE_REFERENCE;
    const hash = safeString(entry.designedFromHash ?? entry.DesignedFromHash).trim().toLowerCase();
    result.push({ key, purpose, ...(hash ? { designedFromHash: hash } : {}) });
  });
  return result;
};

/** The canonical JSON array string the lane round-trips and the definition stores. */
export const serializeDependentAssets = (assets) => JSON.stringify(normalizeDependentAssets(assets));

const assetKindLabel = (kind) => (kind === 'pdf' ? 'PDF' : kind === 'text' ? t('Text') : kind === 'table' ? t('Spreadsheet') : t('Image'));

/** Above this many entries a group is offered through the searchable picker rather than a flyout (AccentTemplateInputUnbound). */
export const ASSET_MENU_PICKER_THRESHOLD = 10;

/**
 * The prompt composer's right-click asset groups for dependent-asset sessions, as two partitions of the library:
 *  - "Assets": every active library asset this customization does NOT yet reference. Inserting writes asset:<key>
 *    into the prompt (the server resolves it through the dependentAssets lane's readSource topics) and the item's
 *    onInsert hook ({ attachImage, notify }) declares it as a design reference (declare(asset) -> bool), attaches
 *    its thumbnail when it has one and tells the transcript what happened.
 *  - "Assets (Referenced)": the assets already in `referenced` ([{ key }], the customization's dependentAssets), so
 *    the menu doubles as a view of what the design depends on; inserting one just inserts (and attaches).
 * Both groups are picker-enabled: past ASSET_MENU_PICKER_THRESHOLD entries the menu opens a searchable list.
 */
export const buildDependentAssetPromptCatalog = (library, { declare, referenced = [] } = {}) => {
  const referencedKeys = new Set((Array.isArray(referenced) ? referenced : []).map((r) => safeString(r?.key).trim()).filter(Boolean));
  const toItem = (a, isReferenced) => {
    const desc = safeString(a.Description).trim();
    const shortDesc = desc.length > 60 ? `${desc.slice(0, 57)}...` : desc;
    const what = `${a.Key} (${assetKindLabel(a.Kind)})`;
    return {
      label: `${what}${shortDesc ? ` - ${shortDesc}` : ''}`,
      search: `${a.Key} ${assetKindLabel(a.Kind)} ${desc}`,
      token: `asset:${a.Key}`,
      snippet: `asset:${a.Key}`,
      onInsert: async ({ attachImage, notify } = {}) => {
        const added = !isReferenced && typeof declare === 'function' ? !!declare(a) : false;
        let attached = false;
        if (a.HasThumbnail && typeof attachImage === 'function') {
          try {
            const url = await fetchCustomizationAssetThumbnail(a.ID, a.ContentHash);
            attached = !!url && !!attachImage(url);
          } catch {
            attached = false;
          }
        }
        if (typeof notify !== 'function') return;
        notify(added
          ? (attached
            ? t('Asset {{what}} inserted, added to this customisation\'s references and its thumbnail attached to the prompt.', { what })
            : t('Asset {{what}} inserted and added to this customisation\'s references.', { what }))
          : (attached
            ? t('Asset {{what}} inserted; its thumbnail is attached to the prompt.', { what })
            : t('Asset {{what}} inserted.', { what })));
      },
    };
  };
  const active = (Array.isArray(library) ? library : []).filter((a) => a && a.Active !== false && safeString(a.Key).trim());
  const available = active.filter((a) => !referencedKeys.has(safeString(a.Key).trim())).map((a) => toItem(a, false));
  const inUse = active.filter((a) => referencedKeys.has(safeString(a.Key).trim())).map((a) => toItem(a, true));
  const groups = [];
  if (available.length > 0) groups.push({ group: 'Assets', label: t('Assets'), picker: true, pickerThreshold: ASSET_MENU_PICKER_THRESHOLD, items: available });
  if (inUse.length > 0) groups.push({ group: 'AssetsReferenced', label: t('Assets (Referenced)'), picker: true, pickerThreshold: ASSET_MENU_PICKER_THRESHOLD, items: inUse });
  return groups;
};

/** Every entry's designedFromHash set to the library's current content hash (entries missing from the library keep theirs). */
export const refreshDesignedFromHashes = (assets, library) => {
  const byKey = new Map((Array.isArray(library) ? library : []).map((a) => [a.Key, a]));
  return normalizeDependentAssets(assets).map((a) => {
    const hash = safeString(byKey.get(a.key)?.ContentHash).trim().toLowerCase();
    return hash ? { ...a, designedFromHash: hash } : a;
  });
};

/** True when the library copy of the asset differs from the hash the design was last checked against. */
export const isDependentAssetStale = (asset, libraryEntry) => {
  const designed = safeString(asset?.designedFromHash).trim().toLowerCase();
  const current = safeString(libraryEntry?.ContentHash).trim().toLowerCase();
  return !!designed && !!current && designed !== current;
};

/** Drafts this long are treated as pasted documentation rather than chat. */
export const LIBRARY_MATERIAL_MIN_CHARS = 1_500;

/**
 * Composer pre-filter: true when a draft looks like material the library should hold (long prose, or structured
 * JSON / YAML / CSV) rather than a conversational message. Text only - images are screenshots of the turn.
 */
export const looksLikeLibraryMaterial = (draft) => {
  const text = safeString(draft).trim();
  if (!text) return false;
  if (text.length >= LIBRARY_MATERIAL_MIN_CHARS) return true;
  if (text.length < 200) return false;
  // JSON object/array
  if (/^[[{][\s\S]*[\]}]$/.test(text)) {
    try { JSON.parse(text); return true; } catch { /* not JSON */ }
  }
  const lines = text.split('\n').filter((l) => l.trim());
  if (lines.length < 5) return false;
  // CSV: most lines share a consistent delimiter count > 0
  const delimiterCounts = (d) => lines.map((l) => l.split(d).length - 1);
  const consistent = (counts) => counts[0] > 0 && counts.filter((c) => c === counts[0]).length >= lines.length * 0.8;
  if (consistent(delimiterCounts(',')) || consistent(delimiterCounts('\t')) || consistent(delimiterCounts(';'))) return true;
  // YAML: most lines are `key:` or `key: value` or `- item`
  const yamlLike = lines.filter((l) => /^\s*(-\s+|[A-Za-z_][\w.-]*\s*:(\s|$))/.test(l)).length;
  return yamlLike >= lines.length * 0.8;
};

/**
 * The assistant's "save as reference" proposal (ChatQueryShapingResponse.ProposedAsset) as the card holds it:
 * { key, contentType, purpose, informed[], description, text }, or null when absent / unusable.
 */
export const normalizeProposedAsset = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const key = safeString(raw.Key ?? raw.key).trim();
  const text = safeString(raw.Text ?? raw.text);
  // The material is either pasted text or one of the session's files (kept from its stored bytes).
  const sessionDocumentId = safeString(raw.SessionDocumentId ?? raw.sessionDocumentId).trim();
  if (!key || (!text.trim() && !sessionDocumentId)) return null;
  const purposeRaw = safeString(raw.Purpose ?? raw.purpose).trim().toLowerCase();
  return {
    key,
    contentType: safeString(raw.ContentType ?? raw.contentType).trim() || 'text/plain',
    purpose: purposeRaw === ASSET_PURPOSE_RUNTIME ? ASSET_PURPOSE_RUNTIME : ASSET_PURPOSE_REFERENCE,
    informed: (Array.isArray(raw.Informed ?? raw.informed) ? (raw.Informed ?? raw.informed) : []).map(safeString).filter(Boolean),
    description: safeString(raw.Description ?? raw.description).trim(),
    text,
    ...(sessionDocumentId ? { sessionDocumentId, sessionDocumentName: safeString(raw.SessionDocumentName ?? raw.sessionDocumentName).trim() } : {}),
  };
};

const contentTypeLabel = (contentType) => {
  switch (safeString(contentType).toLowerCase()) {
    case 'application/json': return 'JSON';
    case 'text/yaml': return 'YAML';
    case 'text/csv': return 'CSV';
    case 'text/markdown': return 'Markdown';
    default: return t('Text');
  }
};

/**
 * The "Save as reference" card under an assistant message: what the assistant proposes to keep from the pasted
 * material (key, kind, size, which outputs it informed, a collapsed preview) with Save / Dismiss. Frozen once
 * decided (`decision` = 'saved' | 'dismissed').
 */
export const ProposedAssetCard = ({ proposal, decision, savedKey, disabled, onDecide }) => {
  const [open, setOpen] = React.useState(false);
  const [working, setWorking] = React.useState(false);
  if (!proposal) return null;
  const decided = decision === 'saved' || decision === 'dismissed';
  const decide = async (accept) => {
    try {
      setWorking(true);
      await onDecide?.(accept);
    } finally {
      setWorking(false);
    }
  };
  return (
    <div data-tagname="proposedAssetCard" style={{ border: '1px solid #c7d2fe', background: '#eef2ff', borderRadius: 8, padding: '8px 10px', fontSize: 12, color: '#1e293b', maxWidth: 560 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
        <LuLibrary size={14} />
        {decision === 'saved'
          ? t('Saved to the asset library as "{{key}}"', { key: savedKey || proposal.key })
          : decision === 'dismissed'
            ? t('Not saved')
            : t('Keep this for later sessions?')}
      </div>
      {!decided && (
        <div style={{ marginTop: 4 }}>
          {proposal.sessionDocumentId
            ? t('The assistant suggests keeping the session file "{{name}}" in the asset library as a {{purpose}}, so the next session reads it from there instead of it being attached again.', {
              name: proposal.sessionDocumentName || proposal.key,
              purpose: proposal.purpose === ASSET_PURPOSE_RUNTIME ? t('runtime asset') : t('design reference'),
            })
            : t('The assistant suggests saving the pasted material to the asset library as a {{purpose}}, so the next session reads it from there instead of it being pasted again.', {
              purpose: proposal.purpose === ASSET_PURPOSE_RUNTIME ? t('runtime asset') : t('design reference'),
            })}
        </div>
      )}
      <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 10px' }}>
        <span style={{ color: '#64748b' }}>{t('Key')}</span><span style={{ fontWeight: 600 }}>{proposal.key}</span>
        {proposal.sessionDocumentId
          ? (<><span style={{ color: '#64748b' }}>{t('File')}</span><span>{proposal.sessionDocumentName || proposal.sessionDocumentId} · {contentTypeLabel(proposal.contentType)}</span></>)
          : (<><span style={{ color: '#64748b' }}>{t('Kind')}</span><span>{contentTypeLabel(proposal.contentType)} · {t('{{count}} characters', { count: proposal.text.length.toLocaleString() })}</span></>)}
        {!!proposal.description && (<><span style={{ color: '#64748b' }}>{t('About')}</span><span>{proposal.description}</span></>)}
        {proposal.informed.length > 0 && (<><span style={{ color: '#64748b' }}>{t('Informed')}</span><span>{proposal.informed.join(', ')}</span></>)}
      </div>
      {!proposal.sessionDocumentId && (
        <button type="button" data-tagname="proposedAssetPreview" onClick={() => setOpen((v) => !v)} style={{ border: 'none', background: 'none', padding: 0, marginTop: 6, color: '#6f42c1', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
          {open ? t('Hide content') : t('Show content')}
        </button>
      )}
      {open && !proposal.sessionDocumentId && (
        <pre style={{ marginTop: 4, marginBottom: 0, maxHeight: 220, overflow: 'auto', fontSize: 11, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 4, padding: 6, whiteSpace: 'pre-wrap' }}>{proposal.text}</pre>
      )}
      {!decided && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <AccentButton size="sm" tagName="proposedAssetSave" disabled={disabled || working} onClick={(e) => { decide(true).finally(() => e?.promise?.()); }}>{t('Save as reference')}</AccentButton>
          <AccentButton size="sm" outline tagName="proposedAssetDismiss" disabled={disabled || working} onClick={(e) => { decide(false).finally(() => e?.promise?.()); }}>{t('Dismiss')}</AccentButton>
        </div>
      )}
    </div>
  );
};

// one in-flight library load at a time; every control on the page shares the result for a moment
let libraryPromise = null;
let libraryLoadedAt = 0;
export const loadAssetLibrary = async (force = false) => {
  const fresh = !force && libraryPromise && Date.now() - libraryLoadedAt < 15_000;
  if (!fresh) {
    libraryLoadedAt = Date.now();
    libraryPromise = listCustomizationAssets().catch(() => []);
  }
  return await libraryPromise;
};

const kindIcon = (kind) => (kind === 'image' ? <LuImage size={13} /> : kind === 'table' ? <LuTable size={13} /> : <LuFileText size={13} />);

/** Picks an existing library entry to attach. Resolves { asset, purpose } or { canceled: true }. */
const PickAssetDlg = ({ library, excludeKeys, allowRuntimePurpose, onClose }) => {
  const [filter, setFilter] = React.useState('');
  const [purpose, setPurpose] = React.useState(ASSET_PURPOSE_REFERENCE);
  const excluded = new Set(excludeKeys ?? []);
  const needle = filter.trim().toLowerCase();
  const rows = (library ?? [])
    .filter((a) => a.Active && !excluded.has(a.Key))
    .filter((a) => !needle || a.Key.toLowerCase().includes(needle) || safeString(a.Description).toLowerCase().includes(needle));

  return (
    <AccentDialog
      headerContent={t('Attach a library asset')}
      onClose={onClose}
      relativeWidth={(d) => Math.min(720, d.width - 40)}
      relativeHeight={(d) => Math.min(620, d.height - 40)}
      actionsContent={[]}
    >
      <div style={{ display: 'grid', gap: 10, height: '100%', gridTemplateRows: 'auto auto 1fr' }}>
        <input
          type="text"
          className="form-control"
          data-tagname="assetPickerFilter"
          placeholder={t('Filter by key or description...')}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        {allowRuntimePurpose && (
          <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
              <input type="radio" name="pickPurpose" checked={purpose === ASSET_PURPOSE_REFERENCE} onChange={() => setPurpose(ASSET_PURPOSE_REFERENCE)} />
              {t('Design reference')}
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
              <input type="radio" name="pickPurpose" checked={purpose === ASSET_PURPOSE_RUNTIME} onChange={() => setPurpose(ASSET_PURPOSE_RUNTIME)} />
              {t('Runtime asset')}
            </label>
          </div>
        )}
        <div style={{ overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 6 }}>
          {rows.length === 0 ? (
            <div style={{ padding: 12, fontSize: 12, color: '#64748b' }}>{t('No matching library assets. Use "Add to library" to upload one.')}</div>
          ) : rows.map((a) => (
            <button
              key={a.ID}
              type="button"
              data-tagname={`pickAsset_${a.Key}`}
              onClick={() => onClose?.({ asset: a, purpose })}
              style={{ display: 'grid', gridTemplateColumns: '20px 1fr auto', gap: 8, alignItems: 'center', width: '100%', textAlign: 'left', padding: '8px 10px', border: 'none', borderBottom: '1px solid #f1f5f9', background: 'none', cursor: 'pointer' }}
            >
              <span style={{ color: '#64748b' }}>{kindIcon(a.Kind)}</span>
              <span style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 12 }}>{a.Key}</div>
                {!!a.Description && <div style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.Description}</div>}
              </span>
              <span style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>{describeAssetMeasure(a)}</span>
            </button>
          ))}
        </div>
      </div>
    </AccentDialog>
  );
};

/**
 * The References row / panel: one chip per dependent asset (kind icon, key, purpose, "changed" when stale, remove),
 * plus "Attach" (pick from the library) and "Add to library" (upload a file - a text file, PDF, spreadsheet or image - and attach
 * it in one go). `assets` is the normalised array; `onChange(nextAssets)` receives the whole list.
 *
 * `allowRuntimePurpose`: the host embeds assets at run time (document templates), so an entry may be a runtime asset.
 * `compact`: the single-row form the AI dialog shows above the composer; otherwise the editor's panel form.
 * `onAdded(asset, purpose)`: optional hook after an upload or pick (the AI dialog announces it in the chat).
 */
export const DependentAssetsCtrl = ({
  assets,
  onChange,
  allowRuntimePurpose = false,
  disabled = false,
  compact = false,
  onAdded,
  // the library as already loaded by the host, when it has one (saves a round trip); the control loads it otherwise
  library: libraryProp,
}) => {
  const [library, setLibrary] = React.useState(Array.isArray(libraryProp) ? libraryProp : null);
  const list = React.useMemo(() => normalizeDependentAssets(assets), [assets]);

  React.useEffect(() => {
    if (Array.isArray(libraryProp)) { setLibrary(libraryProp); return undefined; }
    let cancelled = false;
    loadAssetLibrary().then((rows) => { if (!cancelled) setLibrary(rows); });
    return () => { cancelled = true; };
  }, [libraryProp]);

  const byKey = React.useMemo(() => new Map((library ?? []).map((a) => [a.Key, a])), [library]);

  const attach = React.useCallback((asset, purpose) => {
    if (!asset?.Key) return;
    const next = normalizeDependentAssets(list).filter((a) => a.key !== asset.Key);
    next.push({
      key: asset.Key,
      purpose: purpose === ASSET_PURPOSE_RUNTIME && allowRuntimePurpose ? ASSET_PURPOSE_RUNTIME : ASSET_PURPOSE_REFERENCE,
      ...(asset.ContentHash ? { designedFromHash: safeString(asset.ContentHash).toLowerCase() } : {}),
    });
    onChange?.(next);
    onAdded?.(asset, purpose);
  }, [list, onChange, onAdded, allowRuntimePurpose]);

  const remove = (key) => onChange?.(list.filter((a) => a.key !== key));

  const pick = async (e) => {
    try {
      const rows = await loadAssetLibrary(true);
      setLibrary(rows);
      const result = await showDialog(<PickAssetDlg library={rows} excludeKeys={list.map((a) => a.key)} allowRuntimePurpose={allowRuntimePurpose} />);
      if (result?.canceled || !result?.asset) return;
      attach(result.asset, result.purpose);
    } finally {
      e?.promise?.();
    }
  };

  const upload = async (e) => {
    try {
      const result = await showDialog(<CustomizationAssetDlg mode="any" allowRuntimePurpose={allowRuntimePurpose} />);
      if (result?.canceled || !result?.asset) return;
      const rows = await loadAssetLibrary(true);
      setLibrary(rows);
      attach(result.asset, result.purpose);
    } finally {
      e?.promise?.();
    }
  };

  const chips = list.map((a) => {
    const entry = byKey.get(a.key);
    const missing = library != null && !entry;
    const inactive = !!entry && !entry.Active;
    const stale = !missing && isDependentAssetStale(a, entry);
    const tooltip = [
      entry?.Description,
      a.purpose === ASSET_PURPOSE_RUNTIME ? t('Runtime asset: the output embeds it.') : t('Design reference: the assistant reads it when designing the outputs.'),
      missing ? t('Not in the asset library any more.') : null,
      inactive ? t('Inactive in the asset library.') : null,
      stale ? t('The library copy changed since the design was last checked against it.') : null,
    ].filter(Boolean).join('\n');
    return (
      <span
        key={a.key}
        data-tagname={`dependentAsset_${a.key}`}
        title={tooltip}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 6px 2px 8px', borderRadius: 12, fontSize: 11,
          border: `1px solid ${missing || inactive ? '#fca5a5' : stale ? '#fcd34d' : '#cbd5e1'}`,
          background: missing || inactive ? '#fef2f2' : stale ? '#fffbeb' : '#f8fafc', color: '#334155', maxWidth: 260,
        }}
      >
        <span style={{ color: '#64748b', display: 'inline-flex' }}>{kindIcon(entry?.Kind)}</span>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>{a.key}</span>
        {a.purpose === ASSET_PURPOSE_RUNTIME && <span style={{ color: '#64748b' }}>{t('runtime')}</span>}
        {(missing || inactive) && <span style={{ color: '#b91c1c', fontWeight: 600 }}>{missing ? t('missing') : t('inactive')}</span>}
        {stale && <span style={{ color: '#b45309', fontWeight: 600 }}>{t('changed')}</span>}
        {!disabled && (
          <button
            type="button"
            data-tagname={`removeDependentAsset_${a.key}`}
            title={t('Remove')}
            onClick={() => remove(a.key)}
            style={{ border: 'none', background: 'none', padding: 0, display: 'inline-flex', color: '#64748b', cursor: 'pointer' }}
          >
            <LuX size={12} />
          </button>
        )}
      </span>
    );
  });

  const actionStyle = { display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'none', padding: '2px 4px', color: disabled ? '#94a3b8' : '#6f42c1', fontSize: 11, fontWeight: 600, cursor: disabled ? 'default' : 'pointer' };

  if (compact) {
    return (
      <div data-tagname="dependentAssets" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: '#6c757d', display: 'inline-flex', alignItems: 'center', gap: 4 }} title={t('Library assets this customisation is designed from. The assistant reads them every turn, so documentation never has to be pasted twice.')}>
          <LuPaperclip size={12} /> {t('References')}:
        </span>
        {chips}
        {chips.length === 0 && <span style={{ fontSize: 11, color: '#94a3b8' }}>{t('none')}</span>}
        <button type="button" data-tagname="attachDependentAsset" disabled={disabled} onClick={pick} style={actionStyle} title={t('Attach an existing library asset')}>
          <LuPlus size={12} /> {t('Attach')}
        </button>
        <button type="button" data-tagname="uploadDependentAsset" disabled={disabled} onClick={upload} style={actionStyle} title={t('Upload a file (documentation, a schema, a spreadsheet, a sample) to the asset library and attach it')}>
          <LuLibrary size={12} /> {t('Add to library')}
        </button>
      </div>
    );
  }

  return (
    <div data-tagname="dependentAssets" style={{ display: 'grid', gap: 10 }}>
      <div style={{ fontSize: 12, color: '#475569' }}>
        {allowRuntimePurpose
          ? t('Library assets this customisation depends on: design references the assistant reads when designing it (documentation, schemas, samples, brand guides) and runtime assets the output embeds. A reference marked "changed" was updated in the library since the design last relied on it.')
          : t('Library assets this customisation is designed from: the supplier\'s or business\'s documentation, schemas, field lists and samples. The assistant reads them on every turn, so nothing has to be pasted twice. A reference marked "changed" was updated in the library since the design last relied on it.')}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {chips}
        {chips.length === 0 && <span style={{ fontSize: 12, color: '#94a3b8' }}>{t('No references attached.')}</span>}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <AccentButton size="sm" outline tagName="attachDependentAsset" disabled={disabled} onClick={pick}>{t('Attach from library...')}</AccentButton>
        <AccentButton size="sm" outline tagName="uploadDependentAsset" disabled={disabled} onClick={upload}>{t('Add to library...')}</AccentButton>
      </div>
    </div>
  );
};
